import {
  WIKI_GROUP_TITLE_KEYS,
  renderWikiBlocks,
  wikiDefaultPageId,
  wikiPageById,
  wikiPagesSorted,
  type WikiPageGroup,
  type WikiPageId,
} from "../../../../data/raid-wiki";
import { t } from "../../../../data/strings";

export type RaidWikiPanelApi = {
  dispose: () => void;
};

/**
 * Raid Leader wiki panel — TOC + article.
 * Host must be `#screen-overlay` (full viewport), not `#ui-safe-frame`
 * (FIT letterbox stays phone-width on desktop).
 */
export function mountRaidWikiPanel(opts: {
  host: HTMLElement;
  onBack: () => void;
  initialPageId?: WikiPageId;
}): RaidWikiPanelApi {
  const pages = wikiPagesSorted();
  let currentId: WikiPageId = opts.initialPageId ?? wikiDefaultPageId();
  let disposed = false;

  const root = document.createElement("div");
  root.className = "raid-wiki-panel";
  root.innerHTML = `
    <div class="raid-wiki-top">
      <h1 class="raid-wiki-title" data-wiki-title></h1>
      <button type="button" class="menu-btn raid-wiki-back" data-wiki-back></button>
    </div>
    <div class="raid-wiki-body">
      <nav class="raid-wiki-toc" data-wiki-toc aria-label="wiki toc"></nav>
      <article class="raid-wiki-article" data-wiki-article></article>
    </div>
  `;
  opts.host.appendChild(root);

  const titleEl = root.querySelector("[data-wiki-title]") as HTMLElement;
  const backBtn = root.querySelector("[data-wiki-back]") as HTMLButtonElement;
  const tocEl = root.querySelector("[data-wiki-toc]") as HTMLElement;
  const articleEl = root.querySelector("[data-wiki-article]") as HTMLElement;

  titleEl.textContent = t("raid.wiki.title");
  backBtn.textContent = t("raid.wiki.back");

  backBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (disposed) return;
    opts.onBack();
  });

  function buildToc(): void {
    tocEl.replaceChildren();
    let lastGroup: WikiPageGroup | null = null;
    for (const page of pages) {
      if (page.group !== lastGroup) {
        lastGroup = page.group;
        const g = document.createElement("div");
        g.className = "raid-wiki-toc-group";
        g.textContent = t(WIKI_GROUP_TITLE_KEYS[page.group]);
        tocEl.appendChild(g);
      }
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "raid-wiki-toc-item";
      btn.dataset.wikiPage = page.id;
      btn.textContent = t(page.titleKey);
      if (page.id === currentId) btn.classList.add("is-active");
      btn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        showPage(page.id);
      });
      tocEl.appendChild(btn);
    }
  }

  function showPage(id: WikiPageId): void {
    currentId = id;
    const page = wikiPageById(id);
    articleEl.innerHTML = `
      <h1 class="raid-wiki-page-title">${escapeLocal(t(page.titleKey))}</h1>
      ${renderWikiBlocks(page.blocks)}
    `;
    for (const el of tocEl.querySelectorAll(".raid-wiki-toc-item")) {
      const b = el as HTMLButtonElement;
      b.classList.toggle("is-active", b.dataset.wikiPage === id);
    }
    articleEl.scrollTop = 0;
  }

  buildToc();
  showPage(currentId);

  return {
    dispose: () => {
      if (disposed) return;
      disposed = true;
      root.remove();
    },
  };
}

function escapeLocal(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
